import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

import PickerField from '../PickerField';
import Panel from '../PickerField/Panel';
import Stepper from '../PickerField/Stepper';
import useStyles from '../PickerField/useStyles';

const LABELS = {
  title: 'Set exact time',
  hour: 'Hour',
  minute: 'Minute',
  period: 'Period',
  am: 'AM',
  pm: 'PM',
  quick: 'Quick set minutes',
  cancel: 'Cancel',
  apply: 'Apply',
  increase: 'Increase',
  decrease: 'Decrease',
  placeholder: '–',
};

const MINUTES = { min: 0, max: 59 };

const pad = (number) => String(number).padStart(2, '0');

const now = () => {
  const date = new Date();
  return { hour: date.getHours(), minute: date.getMinutes() };
};

// Time of day as `{ hour: 0–23, minute: 0–59 }` (or `null`)
// with `native`, each part is a native select that emits per pick.
const TimeField = ({
  value,
  onChange,
  hour12,
  quickMinutes,
  labels,
  disabled,
  error,
  className,
}) => {
  const classes = useStyles();
  const anchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(now);
  const text = { ...LABELS, ...labels };
  const periods = [text.am, text.pm];
  const hours = hour12 ? { min: 1, max: 12 } : { min: 0, max: 23 };

  const period = (time) => Number(time.hour >= 12);
  const withPeriod = (time, index) => ({ ...time, hour: (time.hour % 12) + index * 12 });
  const shownHour = (time) => (hour12 ? time.hour % 12 || 12 : time.hour);
  const withHour = (time, hour) =>
    hour12 ? withPeriod({ ...time, hour }, period(time)) : { ...time, hour };
  const formatHour = (hour) => (hour12 ? String(hour) : pad(hour));

  const picked = value || now();
  const tone = !value && classes.unit;

  return (
    <>
      <PickerField
        ref={anchor}
        icon={<AccessTimeIcon />}
        open={open}
        error={error}
        disabled={disabled}
        className={className}
        onClick={() => {
          setDraft(picked);
          setOpen(true);
        }}
      >
        <span className={classnames(classes.part, tone)}>
          {value ? formatHour(shownHour(value)) : text.placeholder}
        </span>
        <span className={classnames(classes.part, tone)}>
          <span>:</span>
          {value ? pad(value.minute) : text.placeholder}
        </span>
        {hour12 && (
          <span className={classnames(classes.part, classes.unit)}>
            {value && periods[period(value)]}
          </span>
        )}
      </PickerField>
      <Panel
        anchorEl={anchor.current}
        open={open}
        labels={text}
        chips={quickMinutes.map((minute) => ({
          label: `:${pad(minute)}`,
          onClick: () => setDraft({ ...draft, minute }),
        }))}
        onClose={() => setOpen(false)}
        onApply={() => {
          onChange(draft);
          setOpen(false);
        }}
      >
        <Stepper
          label={text.hour}
          labels={text}
          value={shownHour(draft)}
          wrap
          pad={2}
          onChange={(hour) => setDraft(withHour(draft, hour))}
          {...hours}
        />
        <Stepper
          label={text.minute}
          labels={text}
          value={draft.minute}
          wrap
          pad={2}
          onChange={(minute) => setDraft({ ...draft, minute })}
          {...MINUTES}
        />
        {hour12 && (
          <div className={classes.control}>
            <span className={classes.caption}>{text.period}:</span>
            <div className={classes.toggle}>
              {periods.map((name, index) => (
                <button
                  key={name}
                  type="button"
                  aria-pressed={index === period(draft)}
                  className={classnames(
                    classes.toggleItem,
                    index === period(draft) && classes.toggleOn
                  )}
                  onClick={() => setDraft(withPeriod(draft, index))}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}
      </Panel>
    </>
  );
};

TimeField.propTypes = {
  value: PropTypes.shape({
    hour: PropTypes.number.isRequired,
    minute: PropTypes.number.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  hour12: PropTypes.bool,
  quickMinutes: PropTypes.arrayOf(PropTypes.number),
  labels: PropTypes.objectOf(PropTypes.string),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  className: PropTypes.string,
};

TimeField.defaultProps = {
  value: null,
  hour12: false,
  quickMinutes: [0, 15, 30, 45],
  labels: LABELS,
  disabled: false,
  error: false,
  className: undefined,
};

export default TimeField;
